import { DASHBOARD_TABS } from './tabDefinitions';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';

const _name = 'TabEngine';

class TabEngine {
    _visibleTabs  = [];
    _allTabs       = [];
    _activeIndex   = 0;
    _tabBarVisible = true;
    _pendingStartTabId = null; // start tab that didn't exist yet (e.g. workspace tab added later)

    // ─── lifecycle ────────────────────────────────────────

    /**
     * Filters tabs by admin role and sets initial active index.
     * Skips if already booted — prevents reset on re-render.
     * Notifies 'tabChanged' so consumers that track the active tab via ComponentNotifier
     * (e.g. JxWindowManager) get the initial value, not just later switches.
     * Called directly from DashboardPage's render body (not an effect), so the notify
     * is deferred via queueMicrotask — broadcasting synchronously here would trigger a
     * forceUpdate in another component (e.g. JxWindowManagerPainter) while React is still
     * mid-render, which throws "Cannot update during an existing state transition".
     * State (_visibleTabs/_activeIndex) is still set synchronously so callers in the same
     * render can read it immediately via getActiveTab()/getVisibleTabs().
     * @param {boolean} isAdmin
     */
    bootup = (isAdmin) => {
        if (this._visibleTabs.length > 0) return;
        this._allTabs     = DASHBOARD_TABS.filter((t) => !t.admin || isAdmin);
        this._visibleTabs = [...this._allTabs];
        this._activeIndex = 0;
        queueMicrotask(() => ComponentNotifier.notify(_name, 'tabChanged', this.getActiveTab()));
    };

    /**
     * Sets the active tab to the configured start tab.
     * Notifies 'tabChanged' — same as setTab() — so JxWindowManager (and any other
     * ComponentNotifier listener) learns the active tab once it's applied, not just
     * on manual tab clicks. Without this, tab-owned floating windows (e.g.
     * FloatingContentPanel) stay invisible until the user manually switches tabs at
     * least once, since JxWindowManager's activeTabId otherwise never gets seeded.
     * @param {string|null} startTabId
     */
    setStartTab = (startTabId) => {
        if (!startTabId) return;
        const index = this._visibleTabs.findIndex((t) => t.id === startTabId);
        if (index < 0) {
            this._pendingStartTabId = startTabId;
            return;
        }
        this._pendingStartTabId = null;
        this._activeIndex = index;
        ComponentNotifier.notify(_name, 'tabChanged', this.getActiveTab());
    };

    // ─── tab visibility ───────────────────────────────────

    /**
     * Hides a tab by id. If it was active, switches to first visible tab.
     * @param {string} id
     */
    hideTab = (id) => {
        this._visibleTabs = this._visibleTabs.filter((t) => t.id !== id);
        this._clampActiveIndex();
        this._notifyTabsChanged();
    };

    /**
     * Shows a previously hidden tab by id, restoring its original position.
     * @param {string} id
     */
    showTab = (id) => {
        if (this._visibleTabs.find((t) => t.id === id)) return; // already visible
        const original = this._allTabs.find((t) => t.id === id);
        if (!original) return;

        // Restore in original order
        const originalIndex = this._allTabs.indexOf(original);
        const insertAt = this._visibleTabs.findIndex((t) => this._allTabs.indexOf(t) > originalIndex);
        if (insertAt < 0) {
            this._visibleTabs.push(original);
        } else {
            this._visibleTabs.splice(insertAt, 0, original);
        }
        this._notifyTabsChanged();
    };

    /**
     * Hides all tab labels (tab bar) without changing active tab.
     */
    hideAllTabs = () => {
        this._tabBarVisible = false;
        this._notifyTabsChanged();
    };

    /**
     * Shows all tab labels (tab bar).
     */
    showAllTabs = () => {
        this._visibleTabs = [...this._allTabs];
        this._tabBarVisible = true;
        this._clampActiveIndex();
        this._notifyTabsChanged();
    };

    /** @returns {boolean} */
    isTabBarVisible = () => this._tabBarVisible;

    // ─── dynamic tabs ─────────────────────────────────────

    /**
     * Inserts a runtime tab (e.g. a workspace) after afterId, or at the end. No-op if the id exists.
     * Applies a pending start tab when this is the tab it was waiting for.
     * @param {object} tab - same shape as DASHBOARD_TABS entries
     * @param {string|null} afterId
     */
    addTab = (tab, afterId = null) => {
        if (this._allTabs.find((t) => t.id === tab.id)) return;
        const activeId = this.getActiveTab()?.id;
        this._insertAfter(this._allTabs, tab, afterId);
        this._insertAfter(this._visibleTabs, tab, afterId);
        this._keepActive(activeId);
        this._notifyTabsChanged();
        if (this._pendingStartTabId === tab.id) this.setStartTab(tab.id);
    };

    /**
     * Removes a runtime tab. If it was active, switches to fallbackId.
     * @param {string} id
     * @param {string|null} fallbackId
     */
    removeTab = (id, fallbackId = null) => {
        const activeId = this.getActiveTab()?.id;
        const wasActive = activeId === id;
        this._allTabs = this._allTabs.filter((t) => t.id !== id);
        this._visibleTabs = this._visibleTabs.filter((t) => t.id !== id);
        this._keepActive(activeId);
        this._clampActiveIndex();
        this._notifyTabsChanged();
        if (wasActive && fallbackId) this.setTabById(fallbackId);
    };

    /**
     * @param {string} id
     * @param {string} label
     */
    renameTab = (id, label) => {
        const tab = this._allTabs.find((t) => t.id === id);
        if (!tab) return;
        tab.label = label;
        this._notifyTabsChanged();
    };

    // ─── public ──────────────────────────────────────────

    /**
     * Sets the active tab by index and notifies consumers.
     * @param {number} index
     */
    setTab = (index) => {
        if (index < 0 || index >= this._visibleTabs.length) return;
        this._activeIndex = index;
        ComponentNotifier.notify(_name, 'tabChanged', this.getActiveTab());
    };

    /**
     * Sets the active tab by id.
     * @param {string} id
     */
    setTabById = (id) => {
        const index = this.getIndexById(id);
        if (index < 0) return;
        this.setTab(index);
    };

    /**
     * Returns the index of a tab by id, or -1 if not found.
     * @param {string} id
     */
    getIndexById = (id) => this._visibleTabs.findIndex((t) => t.id === id);

    /** @returns {object|null} */
    getActiveTab = () => this._visibleTabs[this._activeIndex] ?? null;

    /** @returns {object[]} */
    getVisibleTabs = () => this._visibleTabs;

    /** @returns {number} */
    getActiveIndex = () => this._activeIndex;

    // ─── private ─────────────────────────────────────────

    _clampActiveIndex = () => {
        if (this._activeIndex >= this._visibleTabs.length) {
            this._activeIndex = Math.max(0, this._visibleTabs.length - 1);
        }
    };

    // keeps the same tab active after the list shifted
    _keepActive = (activeId) => {
        const index = this._visibleTabs.findIndex((t) => t.id === activeId);
        if (index >= 0) this._activeIndex = index;
    };

    _insertAfter = (list, tab, afterId) => {
        const index = list.findIndex((t) => t.id === afterId);
        if (index < 0) list.push(tab);
        else list.splice(index + 1, 0, tab);
    };

    _notifyTabsChanged = () => {
        ComponentNotifier.notify(_name, 'tabsChanged', { tabs: this._visibleTabs, activeIndex: this._activeIndex });
    };
}

const tabEngine = new TabEngine();
export default tabEngine;
